/**
 * Pure view helpers for the organizer's roster (§ G4/G10).
 *
 * Same split as `flightOptionsView.ts`: the component renders, this decides what
 * the words are. Kept apart because these are the places the surface can quietly
 * mislead an organizer — a null total rendered as "$0", or a city tab that shows
 * an average when one person in it misses the meeting.
 */
import type { AnchorVerdict, GroupTotal, MemberStatus, OriginGroup, RosterLeg, RosterMember } from '@/lib/api';

export interface VerdictTone {
  label: string;
  /** Semantic token classes — the instrument palette, not raw colors. */
  className: string;
  /** True when the organizer needs to do something about it. */
  needsAttention: boolean;
}

export function verdictTone(v: AnchorVerdict): VerdictTone {
  switch (v) {
    case 'misses':
      return { label: 'Misses the meeting', className: 'text-destructive border-destructive/40 bg-destructive/5', needsAttention: true };
    case 'tight':
      return { label: 'Tight', className: 'text-amber-700 border-amber-500/40 bg-amber-500/5', needsAttention: true };
    case 'no_flight':
      return { label: 'No flight yet', className: 'text-muted-foreground border-dashed border-border', needsAttention: true };
    case 'no_anchor':
      return { label: 'No meeting time set', className: 'text-muted-foreground border-dashed border-border', needsAttention: true };
    case 'unknown_timezone':
      // A flight IS chosen; we just cannot place it on a timeline. Actionable —
      // the fix is adding that airport to the timezone map — so it is flagged.
      return { label: "Can't check arrival", className: 'text-amber-700 border-amber-500/40 bg-amber-500/5', needsAttention: true };
    case 'unknown':
      return { label: 'Arrives before', className: 'text-muted-foreground border-border', needsAttention: false };
    case 'own_transport':
      // § G33 — they told us how they are arriving. Not a gap to chase.
      return { label: 'Own transport', className: 'text-muted-foreground border-border bg-muted/40', needsAttention: false };
    case 'local':
      // § G25 — not a problem to solve. Rendering this as "No flight yet" is what
      // put an Atlanta local on the organizer's chase list for an ATL->ATL flight.
      return { label: 'Already here', className: 'text-muted-foreground border-border bg-muted/40', needsAttention: false };
    case 'clears':
    default:
      return { label: 'Clears', className: 'text-foreground border-border bg-muted/40', needsAttention: false };
  }
}

export function statusLabel(s: MemberStatus): string {
  switch (s) {
    case 'managed':
      return 'You added';
    case 'invited':
      return 'Invited';
    case 'proposed':
      return 'Added themselves';
    case 'joined':
    default:
      return 'Joined';
  }
}

/**
 * Money, or nothing.
 *
 * ⚠️ Never render a null amount as "$0". A group with no recorded fares has an
 * UNKNOWN cost, and "$0" is the one reading that is definitely wrong.
 */
export function formatMoney(amount: number | null | undefined): string | null {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return null;
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

/**
 * What the money line claims (§ G4/G23).
 *
 * Prefers the SERVER's `fare_headline`, which knows the difference between "no
 * fares booked", "fares found but nothing booked", and "nothing shopped yet".
 * The old behaviour collapsed all three into "No fares recorded yet" — which is
 * what a roster showed after a sweep had stored 451 real options, making a
 * working feature indistinguishable from a broken one.
 */
export function totalHeadline(
  total: GroupTotal | null,
  faresVisible: boolean,
  fareHeadline?: string | null
): string {
  if (!faresVisible) return 'Fares hidden for this group';
  if (fareHeadline) return fareHeadline;
  if (!total) return 'No fares shopped yet';
  return total.label;
}

/**
 * "from $126" — the cheapest thing shopping found for one leg, per person.
 *
 * ⚠️ Always carries "from", never a bare figure. This is an ESTIMATE scraped
 * minutes-to-hours ago, not a price anybody is holding, and a bare "$126" next
 * to a booked fare reads as the same kind of fact when it is not.
 */
export function legShoppedNote(leg: Pick<RosterLeg, 'shopped_fare' | 'shopped_options'>): string | null {
  const money = formatMoney(leg.shopped_fare);
  if (money) return `from ${money}`;
  // Options with no price is a real, different state — say it rather than
  // showing nothing, which reads as "we never looked".
  return (leg.shopped_options ?? 0) > 0 ? `${leg.shopped_options} options` : null;
}

/** "New York · 3" — the city, and how many travellers are in it (§ G18). */
export function tabLabel(g: OriginGroup): string {
  return `${g.label} · ${g.travellers}`;
}

/**
 * The airports in play in a metro, for the panel header (§ G18 / R7).
 *
 * R7 resolves codes to city names, and says the codes are information rather
 * than jargon exactly where a city has several airports in play — "who is at
 * Newark and who is at JFK" is a real question on the morning of the meeting.
 * So: shown when there is more than one, suppressed when the code would just be
 * a noisier way of writing the city name we already printed.
 */
export function originHeading(g: OriginGroup): string {
  if (g.origin === '?') return 'Origin not set';
  if (g.airports.length > 1) return `Flying from ${g.label} · ${g.airports.join(' · ')}`;
  return `Flying from ${g.label}`;
}

/**
 * "JFK, or EWR / LGA" — one person's departure airport and their alternatives.
 *
 * Null when there is only one, because "JFK" on its own is already the whole
 * truth and an "or" with nothing after it reads like a bug.
 */
export function memberOriginNote(m: {
  home_airport: string | null;
  legs: { leg_role: string; origin: string }[];
  candidate_origins?: string[];
}): string | null {
  const outbound = m.legs.find((l) => l.leg_role === 'outbound');
  const primary = (outbound?.origin || m.home_airport || '').toUpperCase();
  const alts = (m.candidate_origins ?? []).filter(Boolean);
  if (!primary) return null;
  return alts.length === 0 ? primary : `${primary}, or ${alts.join(' / ')}`;
}

/** A tab shows its PROBLEM, never an average. */
export function tabNeedsAttention(g: OriginGroup): boolean {
  return verdictTone(g.worst_verdict).needsAttention;
}

/**
 * The one-line summary of a person's travel.
 * Renders only what exists — no "TBD" placeholders (D5's rule).
 */
export function memberSummary(m: RosterMember): string {
  const outbound = m.legs.find((l) => l.leg_role === 'outbound');
  const ret = m.legs.find((l) => l.leg_role === 'return');
  const parts: string[] = [];
  if (outbound) parts.push(`${outbound.origin}→${outbound.destination} ${outbound.departure_date}`);
  if (ret) parts.push(`back ${ret.departure_date}`);
  if (parts.length === 0) return 'No flights planned yet';
  return parts.join(' · ');
}

/** Party size, stated only when it is more than one person. */
export function partyNote(m: RosterMember): string | null {
  return m.passengers > 1 ? `${m.passengers} travellers` : null;
}

/**
 * "Wed, Aug 26, 11:00 AM – 2:00 PM" — the meeting, as an organizer says it.
 *
 * ⚠️ The END is part of the headline when there is one (§ G21). A meeting that
 * renders as a single instant reads as "11am onwards", and the return leg's whole
 * floor depends on when the room empties — showing the start alone is how "11 to
 * 2" became "11" everywhere downstream.
 */
export function anchorHeadline(anchor: {
  start_at: string | null;
  timezone: string | null;
  label: string | null;
  end_at?: string | null;
}): string {
  if (!anchor.start_at) return 'No meeting time set';
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  if (anchor.timezone) opts.timeZone = anchor.timezone;
  const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => {
    try {
      return new Date(iso).toLocaleString('en-US', o);
    } catch {
      return new Date(iso).toLocaleString('en-US');
    }
  };
  let when = fmt(anchor.start_at, opts);

  if (anchor.end_at) {
    const timeOnly: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
    if (anchor.timezone) timeOnly.timeZone = anchor.timezone;
    // A meeting that spills into another day gets the whole date on both ends —
    // "11:00 AM – 9:00 AM" would read as an eleven-hour meeting run backwards.
    const sameDay = fmt(anchor.start_at, { ...opts, hour: undefined, minute: undefined }) ===
      fmt(anchor.end_at, { ...opts, hour: undefined, minute: undefined });
    when += ` – ${sameDay ? fmt(anchor.end_at, timeOnly) : fmt(anchor.end_at, opts)}`;
  }

  return anchor.label ? `${anchor.label} · ${when}` : when;
}

/** Organizer's to-do line. Empty string when there is genuinely nothing to chase. */
export function attentionLine(coverage: {
  attendees: number;
  fully_booked: number;
  no_flights: number;
  proposed: number;
  local?: number;
  not_flying?: number;
}): string {
  const bits: string[] = [];
  if (coverage.no_flights > 0) bits.push(`${coverage.no_flights} with no flights`);
  if (coverage.proposed > 0) bits.push(`${coverage.proposed} to confirm`);
  // § G25 — locals are not travelling, so they are neither "unbooked" nor a
  // chase item. Counting them inflated every outstanding number by the size of
  // the home-town contingent.
  const travelling = coverage.attendees - (coverage.local ?? 0) - (coverage.not_flying ?? 0);
  const unbooked = travelling - coverage.fully_booked;
  if (unbooked > 0 && coverage.no_flights !== unbooked) bits.push(`${unbooked} not booked`);
  if ((coverage.local ?? 0) > 0) bits.push(`${coverage.local} already there`);
  // § G33 — stated, not chased. A driver is settled.
  if ((coverage.not_flying ?? 0) > 0) bits.push(`${coverage.not_flying} not flying`);
  return bits.join(' · ');
}

/**
 * What actually happened to an organizer invitation (§ G12).
 *
 * Two different outcomes hide behind the word "invited", and the organizer
 * needs to know which one they got: a trusted-domain colleague can act right
 * now, while everyone else cannot do anything until they sign in. Rendering one
 * generic message for both leaves the inviter wondering why nothing happened.
 */
export function organizerStatusNote(email: string, status: 'pending' | 'active' | 'revoked'): string {
  if (status === 'active') return `${email} can organize this meeting now.`;
  if (status === 'revoked') return `${email} was removed.`;
  return `${email} is invited — they become an organizer when they sign in.`;
}
